import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { inked, toon } from "../toon";
import { closeSeam } from "./geom";
import { buildHelmetHair, HELMET_TAIL_TIE } from "./head-natural";

// The natural face's space helmet (#75, the realistic rider): an EVA helmet
// past the toy's slotted ball and gold cap. Head space, face +z, the same
// footprint as the toy (a 0.265 shell about y 0.02, the ring at -0.24) so
// the strand hair inside still fits: one shell closed over the top, with an
// arched face port edged by a rubber seal; a machined neck ring (a flange,
// a groove, a bearing race) carrying the visor's hinge hubs; and a curved
// gold visor in a frame, parked up over the brow on those hinges. Four
// draws, like the toy's.

/** The shell: its center and radius, and how far down it comes (polar angle from the top). */
const SHELL = { y: 0.02, r: 0.265, bottom: Math.PI * 0.86 };
/**
 * The face port, in angles about the shell: its top (polar angle from the
 * top), its half-width round the front, and the radius of the arch that
 * rounds its top corners.
 */
export const PORT = { top: 0.66, half: 0.95, arch: 0.42 };
/**
 * The visor: its radius (just outside the shell), how far past the port's
 * arch and sides it reaches, and how far down it would come over the port.
 * Parked, it's tipped up by that same depth, so its bottom edge sits on the
 * port's top and its top edge stays forward of the crown.
 */
const VISOR = { r: 0.274, margin: 0.08, depth: 0.46 };
const RING_Y = -0.24;
const COLS = 48;

/** `n` steps from `a` to `b` (both ends included). */
function steps(a: number, b: number, n: number): number[] {
  return Array.from({ length: n + 1 }, (_, k) => a + ((b - a) * k) / n);
}

/** The port's half-width (an azimuth) at polar angle `theta`: 0 above it, rounding into the sides under the arch. */
function portHalf(theta: number, top = PORT.top, half = PORT.half): number {
  if (theta <= top) return 0;
  const t = Math.min(1, (theta - top) / PORT.arch);
  return half * Math.sqrt(1 - (1 - t) ** 2);
}

/** A point on a sphere about the shell's center: polar angle from the top, azimuth from +z toward +x. */
function onSphere(r: number, theta: number, a: number): THREE.Vector3 {
  const s = Math.sin(theta);
  return new THREE.Vector3(r * s * Math.sin(a), SHELL.y + r * Math.cos(theta), r * s * Math.cos(a));
}

/**
 * A patch of a sphere, one row per polar angle in `thetas`: `span(theta)`
 * gives each row's azimuths (from, to). Normals are radial, so the rows'
 * seams don't show.
 */
function spherePatch(r: number, thetas: number[], span: (theta: number) => [number, number], cols = COLS): THREE.BufferGeometry {
  const pos: number[] = [];
  const nrm: number[] = [];
  const uv: number[] = [];
  const index: number[] = [];
  const rows = thetas.length - 1;
  for (let i = 0; i <= rows; i++) {
    const s = i / rows;
    const theta = thetas[i]!;
    const [a0, a1] = span(theta);
    for (let j = 0; j <= cols; j++) {
      const p = onSphere(r, theta, a0 + ((a1 - a0) * j) / cols);
      pos.push(p.x, p.y, p.z);
      const n = p.clone().setY(p.y - SHELL.y).normalize();
      nrm.push(n.x, n.y, n.z);
      uv.push(j / cols, 1 - s);
    }
  }
  const w = cols + 1;
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const a = i * w + j;
      const b = a + w;
      // Wound so the faces point out (away from the center).
      index.push(a, b, a + 1, b, b + 1, a + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(nrm, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  return geo;
}

/** The shell: closed over the top and round the back, open at the face from the arch down. */
export function shellGeometry(): THREE.BufferGeometry {
  // Rows packed tight round the arch, where the port's edge turns.
  const thetas = [...steps(0, PORT.top, 8), ...steps(PORT.top, PORT.top + PORT.arch, 18).slice(1), ...steps(PORT.top + PORT.arch, SHELL.bottom, 14).slice(1)];
  return spherePatch(SHELL.r, thetas, (theta) => {
    const w = portHalf(theta);
    return [w, Math.PI * 2 - w];
  });
}

/** The port's edge, from the bottom of one side up over the arch and down the other. */
function portEdge(r: number): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  const N = 40;
  // Spaced finely near the arch (s squared), where the edge turns.
  const theta = (k: number) => PORT.top + 0.004 + (SHELL.bottom - PORT.top - 0.004) * (k / N) ** 2;
  for (let k = N; k >= 0; k--) out.push(onSphere(r, theta(k), -portHalf(theta(k))));
  for (let k = 1; k <= N; k++) out.push(onSphere(r, theta(k), portHalf(theta(k))));
  return out;
}

/** The rubber: a rolled seal round the port, and a grommet where the ponytail leaves the shell. */
function sealGeometry(): THREE.BufferGeometry {
  const seal = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(portEdge(SHELL.r)), 100, 0.009, 6, false);
  const out = HELMET_TAIL_TIE.clone().setY(HELMET_TAIL_TIE.y - SHELL.y).normalize();
  const grommet = new THREE.TorusGeometry(0.024, 0.008, 8, 18);
  grommet.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), out));
  grommet.translate(out.x * SHELL.r, SHELL.y + out.y * SHELL.r, out.z * SHELL.r);
  return mergeGeometries([seal, grommet])!;
}

/**
 * The neck ring, lathed from a closed profile (radius, height about the
 * ring): the suit's wider bearing race below, a groove, the helmet's flange
 * above with a chamfered lip. Each corner point is doubled so its edges stay
 * crisp. The visor's hinge hubs ride on the same mesh.
 */
export function ringGeometry(): THREE.BufferGeometry {
  const profile: [number, number][] = [
    [0.168, 0.036],
    [0.216, 0.036],
    [0.229, 0.027],
    [0.229, 0.006],
    [0.214, 0.004],
    [0.214, -0.004],
    [0.236, -0.007],
    [0.236, -0.026],
    [0.228, -0.034],
    [0.168, -0.034],
    [0.168, 0.036],
  ];
  const doubled: THREE.Vector2[] = [];
  profile.forEach(([r, y], k) => {
    const v = new THREE.Vector2(r, y);
    doubled.push(v);
    if (k > 0 && k < profile.length - 1) doubled.push(v.clone());
  });
  const SEG = 48;
  // Wound so the faces point out: lathe faces point to the right of the profile's direction.
  const ring = new THREE.LatheGeometry(doubled.reverse(), SEG);
  closeSeam(ring, doubled.length, SEG);
  ring.translate(0, RING_Y, 0);
  const hubs = [1, -1].map((side) => {
    const hub = new THREE.CylinderGeometry(0.028, 0.032, 0.02, 18);
    hub.rotateZ(Math.PI / 2);
    hub.translate(side * (SHELL.r + 0.01), SHELL.y, 0);
    return hub.toNonIndexed();
  });
  return mergeGeometries([ring.toNonIndexed(), ...hubs])!;
}

/** Tips a visor-space geometry up about the hinges (the x axis through the shell's center). */
function parkUp(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  geo.translate(0, -SHELL.y, 0);
  geo.rotateX(-VISOR.depth);
  geo.translate(0, SHELL.y, 0);
  return geo;
}

/** The visor's outline down on the face: the port's arch and sides, a margin past them, cut straight across below. */
function visorSpan(theta: number): [number, number] {
  const w = portHalf(theta, PORT.top - VISOR.margin, PORT.half + VISOR.margin);
  return [-w, w];
}
const VISOR_TOP = PORT.top - VISOR.margin;
const VISOR_BOTTOM = PORT.top + VISOR.depth;

/** The visor: a gold sphere patch over the port, tipped up over the brow. */
export function visorGeometry(): THREE.BufferGeometry {
  const thetas = [...steps(VISOR_TOP, VISOR_TOP + PORT.arch, 16), ...steps(VISOR_TOP + PORT.arch, VISOR_BOTTOM, 6).slice(1)];
  return parkUp(spherePatch(VISOR.r, thetas, visorSpan, 32));
}

/** The visor's frame, in the shell's color: round the arch and sides and along its bottom edge, tipped up with it. */
function visorFrameGeometry(): THREE.BufferGeometry {
  const pts: THREE.Vector3[] = [];
  const N = 30;
  const theta = (k: number) => VISOR_TOP + 0.004 + (VISOR_BOTTOM - VISOR_TOP - 0.004) * (k / N) ** 2;
  for (let k = N; k >= 0; k--) pts.push(onSphere(VISOR.r, theta(k), visorSpan(theta(k))[0]));
  for (let k = 1; k <= N; k++) pts.push(onSphere(VISOR.r, theta(k), visorSpan(theta(k))[1]));
  // Back across the bottom edge on the sphere (a straight chord would cut inside it).
  const w = visorSpan(VISOR_BOTTOM)[1];
  for (let k = 1; k < 16; k++) pts.push(onSphere(VISOR.r, VISOR_BOTTOM, w - (2 * w * k) / 16));
  const frame = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts, true), 100, 0.006, 6, true);
  return parkUp(frame);
}

/** The realistic space helmet with the natural face's strand hair inside it. */
export function buildNaturalSpaceHelmet(color: string, hair: string): THREE.Group {
  const helmet = new THREE.Group();
  helmet.name = "space-helmet";
  const shellMat = toon(color, { roughness: 0.3, side: THREE.DoubleSide });
  const shell = inked(mergeGeometries([shellGeometry().toNonIndexed(), visorFrameGeometry().toNonIndexed()])!, shellMat, 0.015);
  shell.name = "helmet-shell";
  helmet.add(shell);
  const seal = new THREE.Mesh(sealGeometry(), toon("#2b2e36", { roughness: 0.8 }));
  seal.name = "seal";
  seal.castShadow = true;
  helmet.add(seal);
  const ring = inked(ringGeometry(), toon("#9aa3b5", { roughness: 0.3, metalness: 0.6 }, "metal"), 0.012);
  ring.name = "neck-ring";
  helmet.add(ring);
  const visor = new THREE.Mesh(visorGeometry(), toon("#e3b341", { roughness: 0.12, metalness: 0.85, side: THREE.DoubleSide }, "metal"));
  visor.name = "visor";
  helmet.add(visor);
  for (const part of buildHelmetHair(hair)) helmet.add(part);
  return helmet;
}
